import { cloneDeep } from "lodash";
import React, { useState } from "react";
import Editor from "react-simple-code-editor";

import { TriangleAlert } from "lucide-react";

import { highlight, languages } from "@/lib/prism";

import { Button } from "@/design-system/components/Button/Button";
import {
  Dialog,
  DialogActionsWrapper,
} from "@/design-system/components/Dialog/Dialog";
import { EditableProject } from "../reducers/environments";

const TEXTAREA_CLASS =
  "mt-1 min-h-[120px] w-full resize-y rounded border bg-gray-50 p-3 font-mono text-xs text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none";

type ModalState = {
  title: string;
  placeholder: string;
  project: EditableProject;
  closeOnOverlay: boolean;
  addingNewProject?: boolean;
  error?: string;
  addingNewEnvironment?: boolean;
  onClose?: () => void;
  onSave: (updatedProject: EditableProject) => void;
};

export const AdvancedConfigProjectModal: React.FC<ModalState> = ({
  project,
  onSave,
  title,
  onClose,
  error,
}) => {
  const [newProjectState] = useState(cloneDeep(project));
  const [initialDocumentLoadCode, setInitialDocumentLoadCode] = useState(
    project.codeEval?.onPageLoad ?? "",
  );
  const [pageChangeCode, setPageChangeCode] = useState(
    project.codeEval?.onPageChange ?? "",
  );
  const [onIdentify, setOnIdentify] = useState(
    project.codeEval?.onIdentify ?? "",
  );
  const [onTrack, setOnTrack] = useState(project.codeEval?.onTrack ?? "");

  const onConfirm = () => {
    newProjectState.codeEval = {
      onPageLoad: initialDocumentLoadCode,
      onPageChange: pageChangeCode,
      onIdentify: onIdentify,
      onTrack: onTrack,
    };
    onSave(newProjectState);
  };

  const hardCodedProps = {
    onIdentify: {
      userID: "The unique identifier of the user.",
      userProps: "User properties passed to the identify call.",
    },
    onTrack: {
      eventName: "The name of the event.",
      eventProperties: "Event properties passed to the track call.",
      builtinProperties: "Properties Freshpaint computes for each event.",
      eventOptions: "Event options passed to manual track calls.",
    },
  };

  const formatHardCodedProps = (key: keyof typeof hardCodedProps) => {
    return (
      `// These variables are available in your code:\n\n` +
      Object.entries(hardCodedProps[key])
        .map(([varName, description]) => `var ${varName}; // ${description}`)
        .join("\n")
    );
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose?.();
  };

  return (
    <Dialog
      open={true}
      onOpenChange={handleOpenChange}
      title={`${title} — ${project.name}`}
      contentProps={{ className: "w-[600px]" }}
    >
      <div className="flex flex-col gap-4">
        {error && (
          <div className="flex items-center gap-2 rounded bg-red-500 px-3 py-2 text-sm font-bold text-white">
            <TriangleAlert className="h-4 w-4 shrink-0 text-white" />
            {error}
          </div>
        )}

        <div>
          <h3 className="text-base font-semibold text-gray-800">Tag Manager</h3>
          <p className="text-textSubtle text-sm">
            Our tag manager feature allows you to inject code directly on your
            website.
          </p>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-900">
            On Initial Document Load:
          </label>
          <Editor
            className={TEXTAREA_CLASS}
            value={initialDocumentLoadCode}
            highlight={(code) => highlight(code, languages.js, "JavaScript")}
            onValueChange={setInitialDocumentLoadCode}
            placeholder="Javascript code..."
            spellCheck={false}
          />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-900">
            On Page Change:
          </label>
          <Editor
            className={TEXTAREA_CLASS}
            value={pageChangeCode}
            highlight={(code) => highlight(code, languages.js, "JavaScript")}
            onValueChange={setPageChangeCode}
            placeholder="Javascript code..."
            spellCheck={false}
          />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-900">
            On Identify:
          </label>
          <pre className="mt-1 overflow-auto rounded border bg-gray-50 p-3 font-mono text-xs text-gray-700">
            <code>{formatHardCodedProps("onIdentify")}</code>
          </pre>
          <Editor
            className={TEXTAREA_CLASS}
            value={onIdentify}
            highlight={(code) => highlight(code, languages.js, "JavaScript")}
            onValueChange={setOnIdentify}
            placeholder="Javascript code..."
            spellCheck={false}
          />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-900">On Track:</label>
          <pre className="mt-1 overflow-auto rounded border bg-gray-50 p-3 font-mono text-xs text-gray-700">
            <code>{formatHardCodedProps("onTrack")}</code>
          </pre>
          <Editor
            className={TEXTAREA_CLASS}
            value={onTrack}
            highlight={(code) => highlight(code, languages.js, "JavaScript")}
            onValueChange={setOnTrack}
            placeholder="Javascript code..."
            spellCheck={false}
          />
        </div>

        <DialogActionsWrapper>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onConfirm}>Save</Button>
        </DialogActionsWrapper>
      </div>
    </Dialog>
  );
};
